import { observable, configure, action, toJS } from 'mobx';
import LocalModel from './localModel';
import util from 'preact-util';

configure({ enforceActions: 'always' });

class BroadcastEventStore extends LocalModel {
    constructor() {
        super('broadcastEvent', {
            namePlural: 'broadcastEvents',
            namePluralReal: 'broadcastevents',
            sort: 'scheduledStart',
            limit: 500,
            api: {
                search: {
                    url: '/api/broadcastevents/',
                    params: {
                        limit: 500,
                        sort: 'scheduledStart',
                    },
                },
                load: {
                    url: '/api/broadcastevents/',
                    params: {},
                },
                save: {
                    url: '/api/broadcastevents/',
                    params: {},
                },
                delete: {
                    url: '/api/broadcastevents/',
                    params: {},
                },
            },
        });
    }

    @observable newBroadcastEvent = {};

    @observable broadcastEvent = {};

    @observable broadcastEvents = [];

    @observable draggedEvent = null;

    @observable resizingEvent = null;

    @action
    setDraggedEvent(event) {
        this.draggedEvent = event;
    }

    @action
    setResizingEvent(event) {
        this.resizingEvent = event;
    }

    @action
    async loadEvents(params = {}) {
        try {
            const response = await util.fetchApi('/api/broadcastevents/', { method: 'GET' }, params);
            if (response && response.status === 200) {
                this.updateKeyValue('broadcastEvents', response.data || []);
                return response.data;
            }
        } catch (err) {
            console.error('Error loading broadcast events:', err);
        }
        return [];
    }

    @action
    async moveEvent(id, scheduledStart, channel) {
        try {
            const body = { scheduledStart };
            if (channel) {
                body.channel = channel;
            }
            const response = await util.fetchApi(`/api/broadcastevents/${id}/move`, { method: 'POST' }, body);
            if (response && response.status === 200) {
                // Update local events with the moved event and affected events
                const events = toJS(this.broadcastEvents);
                const movedEvent = response.data.broadcastEvent;
                const affectedEvents = response.data.affectedEvents || [];

                // Update the moved event
                const movedIdx = events.findIndex(e => e.id === movedEvent.id);
                if (movedIdx >= 0) {
                    events[movedIdx] = movedEvent;
                }

                // Update affected events
                affectedEvents.forEach(affected => {
                    const idx = events.findIndex(e => e.id === affected.id);
                    if (idx >= 0) {
                        events[idx] = { ...events[idx], scheduledStart: affected.scheduledStart, scheduledEnd: affected.scheduledEnd };
                    }
                });

                this.updateKeyValue('broadcastEvents', events);
            }
            return response;
        } catch (err) {
            console.error('Error moving broadcast event:', err);
            return null;
        }
    }

    @action
    async resizeEvent(id, scheduledEnd) {
        try {
            const response = await util.fetchApi(`/api/broadcastevents/${id}/resize`, { method: 'POST' }, { scheduledEnd });
            if (response && response.status === 200) {
                const events = toJS(this.broadcastEvents);
                const resizedEvent = response.data.broadcastEvent;
                const idx = events.findIndex(e => e.id === resizedEvent.id);
                if (idx >= 0) {
                    events[idx] = resizedEvent;
                }
                this.updateKeyValue('broadcastEvents', events);
            }
            return response;
        } catch (err) {
            console.error('Error resizing broadcast event:', err);
            return null;
        }
    }

    // Get events for a specific time range
    getEventsInRange(startDate, endDate, channelId) {
        const events = toJS(this.broadcastEvents);
        const start = new Date(startDate);
        const end = new Date(endDate);

        return events.filter(e => {
            if (channelId && e.channel !== channelId) return false;
            const eventStart = new Date(e.scheduledStart);
            const eventEnd = new Date(e.scheduledEnd);
            return eventStart < end && eventEnd > start;
        });
    }

    // Get events for a specific hour
    getEventsForHour(date, hour, channelId) {
        const start = new Date(date);
        start.setHours(hour, 0, 0, 0);
        const end = new Date(date);
        end.setHours(hour + 1, 0, 0, 0);
        return this.getEventsInRange(start, end, channelId);
    }
}

const store = new BroadcastEventStore();
export default store;
